import React, { useEffect, useRef, useState } from 'react';
import { Add, Remove } from '@material-ui/icons';
import { renderAsync } from 'docx-preview';
import './WordViewer.styles.css';
const styles = {
  container: {
    height: '68vh',
    display: 'flex',
    flexDirection: 'column',
  },
  zoomControls: {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    border: '1px solid rgba(0,0,0,0.2)',
    borderRadius: '4px 4px 0 0',
  },
  button: {
    cursor: 'pointer',
    color: '#4b5563',
    margin: 0,
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
  },
  text: {
    margin: '0 8px',
    minWidth: '40px',
    textAlign: 'center',
  },
  documentArea: {
    flex: 1,
    overflow: 'auto',
    padding: '0',
    position: 'relative',
    userSelect: 'none',
  },
  errorMessage: {
    color: 'red',
    padding: '20px',
    textAlign: 'center',
  },
  refWrapper: (zoom) => ({
    transform: `scale(${zoom})`,
    transformOrigin: 'top center',
  }),
};

const WordViewer = ({ url, onLoad = () => null }) => {
  const containerRef = useRef();
  const [zoom, setZoom] = useState(1);
  const [error, setError] = useState(null);

  useEffect(() => {
    const abortController = new AbortController();

    const loadDocument = async () => {
      try {
        const response = await fetch(url, { signal: abortController.signal });

        if (!response.ok) {
          throw new Error(`HTTP error! status: ${response.status}`);
        }

        const contentType = response.headers.get('Content-Type');
        if (
          !contentType.includes(
            'application/vnd.openxmlformats-officedocument.wordprocessingml.document'
          )
        ) {
          throw new Error('File is not a valid Word document (.docx)');
        }

        const blob = await response.blob();

        // Clean the container before rendering
        if (containerRef.current) {
          containerRef.current.innerHTML = '';
        }

        await renderAsync(blob, containerRef.current, null, {
          className: 'docx',
          breakPages: true,
          inWrapper: true,
          ignoreLastRenderedPageBreak: false,
          trimXmlDeclaration: true,
        });

        onLoad();
        setError(null);
      } catch (err) {
        if (err.name !== 'AbortError') {
          setError(err.message);
          console.error('Error loading Word document:', err);
          if (containerRef.current) {
            containerRef.current.innerHTML = `<p style='color:red; padding: 20px;'>Error loading the document</p>`;
          }
        }
      }
    };

    loadDocument();

    return () => {
      abortController.abort();
    };
  }, [url, onLoad]);

  return (
    <div style={styles.container}>
      {/* zoom controls */}
      <div style={styles.zoomControls}>
        <button
          onClick={() => setZoom((prev) => Math.max(0.5, prev - 0.1))}
          style={styles.button}
          aria-label="ZoomOut"
        >
          <Remove fontSize="1rem" />
        </button>
        <span style={styles.text}>{Math.round(zoom * 100)}%</span>
        <button
          onClick={() => setZoom((prev) => Math.min(2, prev + 0.1))}
          style={styles.button}
          aria-label="ZoomIn"
        >
          <Add fontSize="1rem" />
        </button>
      </div>

      {/* Document area */}
      <div style={styles.documentArea}>
        {error ? (
          <div style={styles.errorMessage}>{error}</div>
        ) : (
          <div style={styles.refWrapper(zoom)}>
            <div ref={containerRef} />
          </div>
        )}
      </div>
    </div>
  );
};

export default WordViewer;
